import { tz } from "@date-fns/tz";
import {
  extrudeAdditionalInfo,
  formatDraftReservationDate,
  formatDurationToDays,
  formatReservationDate,
  isMobileDevice,
  isWeb,
  useServiceCalendar,
  useToday,
  useV2GetCheckoutQuery,
  useValidateAddressInLocalizationMutation,
} from "@stepapp/core";
import { CategoryType } from "@stepapp/core/apiTypes";
import { Block, Divider, Typography } from "@stepapp/ui";
import { addDays, addWeeks, endOfWeek, format, isBefore, isSameWeek, startOfWeek } from "date-fns";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  AddressForm,
  HomeTypeQuestion,
  HouseAreaOutOfScopeSheet,
  LocationDisclaimerSheet,
  PresenceInfoQuestion,
  ServiceFooter,
  ServiceNotice,
  ServiceSceneDescription,
  ServiceWeekCalendar,
} from "../../../src/components";
import { useAppDispatch, useAppSelector, useCreateOrUpdateAddress } from "../../../src/hooks";
import { setEndDate, setStartDate } from "../../../src/store/calendar";
import { setDraftDate } from "../../../src/store/draftReservation";
import type { ReservationFormParams } from "../../../src/store/draftReservation/types";

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: skip
const ServiceDateAndAddressScreen = () => {
  const { t } = useTranslation();
  const today = useToday();
  const appDispatch = useAppDispatch();
  const { navigate } = useRouter();
  const { slug = "" } = useLocalSearchParams<{ slug: string }>();
  const userId = useAppSelector(({ user }) => user?.id);
  const properties = useAppSelector(({ user }) => user.properties);
  const { city, localizationId } = useAppSelector(({ appConfig }) => appConfig);
  const calendarStartDate = useAppSelector(({ calendar }) => calendar.startDate);
  const calendarEndDate = useAppSelector(({ calendar }) => calendar.endDate);
  const configTimezone = useAppSelector(({ appConfig }) => appConfig.timezone);

  const [canChangeWeek, setCanChangeWeek] = useState(true);

  const { data, loading: checkoutLoading } = useV2GetCheckoutQuery();

  const isOneTime = !formatDurationToDays(data?.v2GetCheckout.periodicity?.cycleDuration || "P0D");

  const [validateAddress, { loading: validateAddressLoading }] =
    useValidateAddressInLocalizationMutation();
  const [notAvailableLocation, setNotAvailableLocation] = useState(false);

  const {
    handleSubmit,
    setValue,
    watch,
    trigger,
    formState: { errors },
  } = useFormContext<ReservationFormParams>();
  const { meta, propertyId, address, startDate, locationId } = watch();

  const property = properties?.find((property) => property?.propertyId === propertyId);
  const parsedProperty = {
    ...property,
    type: property?.homeType,
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: skip
  const shouldUpdate = useMemo(() => {
    if (!address) {
      return false;
    }

    return Object.entries(address).every(([key, value]) => {
      if (key === "notes" || key === "doorCode") {
        return true;
      }

      return parsedProperty[key as keyof typeof parsedProperty] === value;
    });
  }, [
    address?.city,
    address?.doorCode,
    address?.floorArea,
    address?.postalOrZipCode,
    address?.streetAddress1,
    address?.streetAddress2,
  ]);

  const [createOrUpdateAddress, { loading }] = useCreateOrUpdateAddress({
    property: shouldUpdate ? property : undefined,
    localizationId: locationId,
    isUserLogged: !!userId,
  });

  const handleSaveAddress = useCallback(async () => {
    const presenceNote = meta?.willCustomerBePresent
      ? `${t("pages.serviceDateAndAddress.iWillBeThere")}`
      : meta?.keyInfo || "";

    const newValues = {
      ...property,
      ...address,
      notes: `${extrudeAdditionalInfo(address?.notes) || ""}\n\n${presenceNote}`,
      city: address?.city || city,
      floorArea: address?.floorArea || 0,
    };

    const isSuccess = await createOrUpdateAddress(newValues);

    isSuccess && setValue("propertyId", isSuccess?.propertyId);
  }, [
    setValue,
    createOrUpdateAddress,
    meta?.willCustomerBePresent,
    meta?.keyInfo,
    property,
    city,
    address,
    t,
  ]);

  const onSubmit = handleSubmit(
    async ({ address, meta, startDate }) => {
      const presenceNote = meta?.willCustomerBePresent
        ? `${t("pages.serviceDateAndAddress.iWillBeThere")}`
        : meta?.keyInfo || "";

      appDispatch(setDraftDate(startDate as string));
      setValue(
        "address.notes",
        `${extrudeAdditionalInfo(address?.notes) || ""}\n\n${presenceNote}`,
      );

      const { data } = await validateAddress({
        variables: {
          payload: {
            city: address?.city || "",
            localizationId,
            postalOrZipCode: address?.postalOrZipCode || "",
            streetAddress1: address?.streetAddress1 || "",
            streetAddress2: address?.streetAddress2,
          },
        },
      });

      if (!data?.validateAddressInLocalization) return setNotAvailableLocation(true);

      !!userId && handleSaveAddress();

      navigate(`/service/${slug}/summary`);
    },
    async (_error) => {
      await trigger();
      if (isWeb) {
        const div = document.querySelectorAll("#form-has-error");
        div?.[0]?.parentElement?.parentElement?.scrollIntoView({
          behavior: "smooth",
          block: isMobileDevice ? "nearest" : "center",
        });
      }
    },
  );

  const isLoading = meta.loading || loading || validateAddressLoading;
  const initialDate = startDate && formatReservationDate(startDate, "yyyy-MM-dd'T'HH:mm:ssXXX");
  const requirePresenceInfo = CategoryType.Massage !== data?.v2GetCheckout?.bundle?.categoryType;

  const {
    selectedDate,
    handleDayPress,
    handleHourPress,
    hours,
    markedDates,
    selectedHour,
    loading: calendarLoading,
    resetDateAndHour,
  } = useServiceCalendar({
    bundleSlug: slug,
    date: initialDate || "",
    timeConsumption: meta.timeConsumption || "",
    locationId,
    calendarEndDate,
    calendarStartDate,
  });

  const errorMsg = useMemo(
    () =>
      !!errors.startDate &&
      (selectedDate ? (selectedHour ? undefined : t("validation.hour")) : t("validation.date")),
    [selectedDate, selectedHour, errors.startDate, t],
  );

  useEffect(() => {
    if (selectedHour && selectedDate) {
      const newDate = formatDraftReservationDate(`${selectedDate} ${selectedHour}`);
      setValue("startDate", newDate, { shouldValidate: true });
      return;
    }
    setValue("startDate", null);
  }, [selectedDate, selectedHour, setValue]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: skip
  const handleShowDetails = useCallback((showDetails: boolean) => {
    setValue("meta.detailsShown", showDetails);
  }, []);

  const handleStartDay = useCallback(() => {
    const tomorrow = addDays(today, 1).toString();

    if (initialDate) {
      const isPastDay = isBefore(initialDate, tomorrow);
      return isPastDay ? tomorrow : startOfWeek(initialDate).toString();
    }
    return tomorrow;
  }, [today, initialDate]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: skip
  useEffect(() => {
    const selectedStartDay = handleStartDay();
    const lastWeekDay = endOfWeek(selectedStartDay).toString();

    appDispatch(setStartDate(format(selectedStartDay, "yyyy-MM-dd")));
    appDispatch(setEndDate(format(lastWeekDay, "yyyy-MM-dd")));
  }, [appDispatch]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: skip
  useEffect(() => {
    if (calendarLoading) return;

    const isFirstWeekFetch =
      isSameWeek(today, calendarStartDate, { in: tz(configTimezone) }) &&
      Object.keys(markedDates).length === 0;

    if (isFirstWeekFetch && canChangeWeek) {
      setCanChangeWeek(false);
      const nextWeekStartDate = startOfWeek(addWeeks(today, 1)).toISOString();

      const nextWeekEndDate = endOfWeek(addWeeks(today, 1)).toISOString();

      appDispatch(setStartDate(format(nextWeekStartDate, "yyyy-MM-dd")));
      appDispatch(setEndDate(format(nextWeekEndDate, "yyyy-MM-dd")));
    }
  }, [calendarLoading]);

  return (
    <Block id="container">
      <Block marginVertical={2}>
        <Typography variant="newH2">{t("pages.serviceDateAndAddress.title")}</Typography>

        <ServiceSceneDescription description={t("pages.serviceDateAndAddress.description")} />
      </Block>

      <ServiceWeekCalendar
        hours={hours}
        startDate={startDate || ""}
        resetSelectedDate={resetDateAndHour}
        handleDayPress={handleDayPress}
        handleHourPress={handleHourPress}
        markedDates={markedDates}
        selectedDate={selectedDate}
        selectedHour={selectedHour}
        errorMsg={errorMsg || undefined}
        loading={calendarLoading || checkoutLoading}
        isReservationFlow={true}
      />

      <ServiceNotice type={isOneTime ? "oneTime" : "subscription"} />

      <ServiceNotice type="date" />

      <Divider spaceless />

      <Block marginVertical={3}>
        <Typography variant="body">{t("pages.serviceDateAndAddress.addAddress")}</Typography>

        <ServiceSceneDescription
          description={t("pages.serviceDateAndAddress.addAddressDescription", {
            context: data?.v2GetCheckout?.bundle?.categoryType?.toLowerCase(),
          })}
        />
      </Block>

      <AddressForm
        city={address?.city || ""}
        doorCode={address?.doorCode || ""}
        errors={errors}
        floorArea={address?.floorArea || 0}
        notes={extrudeAdditionalInfo(address?.notes) || ""}
        postalOrZipCode={address?.postalOrZipCode || ""}
        setValue={setValue}
        trigger={trigger}
        streetAddress1={address?.streetAddress1 || ""}
        streetAddress2={address?.streetAddress2 || ""}
      />

      {requirePresenceInfo && (
        <>
          <Divider />

          <HomeTypeQuestion
            setValue={setValue}
            errorMsg={errors?.meta?.homeTypePlaceholder?.message}
            homeType={meta?.homeTypePlaceholder || ""}
          />

          <PresenceInfoQuestion
            errors={errors}
            keyInfo={meta?.keyInfo}
            setValue={setValue}
            willCustomerBePresent={meta?.willCustomerBePresent}
          />
        </>
      )}
      <ServiceFooter
        showDetails
        handleShowDetails={() => handleShowDetails(true)}
        disabled={isLoading}
        label={t("common.continue")}
        loading={isLoading}
        onPress={onSubmit}
        price={meta.total || ""}
      />

      <LocationDisclaimerSheet
        isVisible={notAvailableLocation}
        setModalVisible={setNotAvailableLocation}
      />

      <HouseAreaOutOfScopeSheet formError={errors?.houseArea} />
    </Block>
  );
};

export default ServiceDateAndAddressScreen;
